import * as React from 'react';

import Upload from 'antd/lib/upload';
import Modal from 'antd/lib/modal';
import { LoadingOutlined, PlusSquareOutlined } from '@ant-design/icons';

import Button from '@common/react/components/Forms/Button';
import {FileType, FileInterface} from '@common/typescript/objects/FileInterface';

import {request} from '@app/components/Api';
import ImageCropper from '@app/components/UI/PetMemorialModal/ImageCropper';

interface FileState {
	isLoading: boolean;
	error: string | null;
}

interface BaseFileProps {
	type: string;
	objectId: number;
	onError?: (error: string) => void;
	fileType?: FileType;
	infoMessage?: string;
	asButton?: boolean;
	accept?: string;
	buttonCaption?: string | JSX.Element;
	buttonClassName?: string;
	disabled?: boolean;
	multiple?: boolean;
	noRelation?: boolean; // Whether to create relation for uploaded file or not
	additionalData?: object;
	transformData?: (data: FormData) => FormData; // Transform data before sending to server
}

interface FileProps extends BaseFileProps {
	update: (data: FileInterface) => void;
	showError?: boolean;
	aspectRatio?: number;
	maxFile: number;
	field: any;
	fileNote: string;
}

interface CustomRequestParams {
	action: any;
	data: any;
	file: any;
	filename: any;
	headers: any;
	onError: any;
	onProgress: any;
	onSuccess: any;
	withCredentials: any;
}

interface UploadFileParams {
	file: any;
	type: string;
	objectId: number;
	fileType: FileType;
	additionalData?: object;
	headers?: any;
}

export const uploadFile = (params: UploadFileParams, transformData?: (data: FormData) => FormData): Promise<any> => {
	const formData = new FormData();
	formData.append('file', params.file);
	formData.append('objectType', params.type);
	formData.append('objectId', params.objectId.toString());
	formData.append('fileType', params.fileType.toString());

	if (params.additionalData) {
		const data = params.additionalData;
		Object.keys(data).forEach(key => formData.append(`data[${key}]`, data[key].toString()));
	}
	return fetch('fineUploader', {
		credentials: 'same-origin',
		method: 'POST',
		headers: params.headers,
		body: transformData ? transformData(formData) : formData
	})
		.then(response => response.json())
		.then(((response) => {
			if (!response.success) throw response.response;
			return response.response;
		}))
		.catch((error) => {
			console.log(error.message);
			throw error;
		});
};

export default class FileUpload extends React.Component<FileProps, any> {
	public static defaultProps: Partial<FileProps> = {
		infoMessage: 'Image size must not exceed 1 Mb',
		asButton: false,
		accept: '',
		buttonCaption: 'Upload',
		objectId: -1,
		showError: true,
	};

	constructor(props : FileProps) {
		super(props);

		this.state = {
			isLoading: false,
			error: null,
			cropperModalOpen: false,
			image: '',
			initFile: {},
			croppedImage: {}
		};

		this.customRequest = this.customRequest.bind(this);
	}

	customRequest(argument) {
		const {file, headers} = argument as CustomRequestParams;
		const {type, objectId, fileType = FileType.Avatar, additionalData, noRelation, transformData} = this.props;

		this.setState({
			isLoading: true,
			error: null,
		});
		uploadFile({
			file,
			fileType,
			objectId,
			type,
			additionalData,
			headers
		}, transformData)
			.then((data: FileInterface) => {
				this.setState({ isLoading: false });
				this.props.update(data);
			}).catch((error) => {
				this.setState({
					isLoading: false,
					error: error.message
				});

				if (this.props.onError) {
					this.props.onError(error.message);
				}
			});
	}

	beforeUpload = (file) => {
		const fileTypes = ['image/jpeg', 'image/jpg', 'image/png'];
		const imgExtensions = ['jpg', 'jpeg', 'png'];

		if (file.size < this.props.maxFile && fileTypes.some(item => item === file.type)
			&& imgExtensions.some(item => file.name.toLowerCase().includes(item))) {
			this.setState({cropperModalOpen: true});
			this.setState({image: file});
			this.setState({initFile: file});
		} if (file.size > this.props.maxFile) {
			this.props.field.form.setFieldError('filePath', 'Image max file size is 10MB - Please upload a smaller image');
			this.props.field.form.setFieldTouched(this.props.field.field.name, true, false);

		} if (!fileTypes.some(item => item === file.type) || !imgExtensions.some(item => file.name.toLowerCase().includes(item))) {
			this.props.field.form.setFieldError('filePath', 'Image required to be in JPG or PNG format');
			this.props.field.form.setFieldTouched(this.props.field.field.name, true, false);
		}

		return false;
	}

	render(): JSX.Element {
		const {asButton, accept, infoMessage, buttonCaption, buttonClassName} = this.props;
		let uploadButton;

		if (asButton) {
			uploadButton = <React.Fragment>
				<Button type="button" className={buttonClassName} isLoading={this.state.isLoading} disabled={this.props.disabled}>
					{buttonCaption}
					<p className={'note'}>
						{this.props.fileNote}
					</p>
				</Button>
				{this.props.showError !== false && <div className="file-upload-error">{this.state.error}</div>}
			</React.Fragment>;
		} else {
			uploadButton = (
				<React.Fragment>
					{this.state.isLoading ? <LoadingOutlined /> : <PlusSquareOutlined />}
					<div className="ant-upload-text">{buttonCaption}</div>
					<div className="ant-upload-info">{this.state.error || infoMessage}</div>
				</React.Fragment>
			);
		}
		return (
			<>
				<Upload
					className={`avatar-uploader ${this.state.error ? 'avatar-uploader_error' : ''}`}
					listType={this.props.asButton ? 'text' : 'picture-card'}
					showUploadList={false}
					accept={accept}
					customRequest={this.customRequest}
					disabled={this.props.disabled}
					multiple={this.props.multiple}
					// @ts-ignore
					beforeUpload={this.beforeUpload}
				>
					{uploadButton}
				</Upload>

				<Modal
					wrapClassName="pet-mem-modal pet-mem-modal_form modal-wrap cropper-wrap"
					visible={this.state.cropperModalOpen}
					onCancel={() => this.setState({cropperModalOpen: false})}
					width={1037}
					centered
				>
					<div>
						<ImageCropper
							image={this.state.image}
							setImage={this.setState}
							initFile={this.state.initFile}
							setCroppedImage={arg => this.setState(arg)}
							setCropperModalOpen={arg => this.setState(arg)}
							uploadFile={arg => this.customRequest(arg)}
							aspectRatio={this.props.aspectRatio}
						/>
					</div>
				</Modal>
			</>
		);
	}}